import { DOCUMENT } from '@angular/common';
import {
  booleanAttribute,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { fromEvent, tap } from 'rxjs';

export interface ScrollInfo {
  top: number;
  atBottom: boolean;
}

@Directive({
  selector: '[iwInfiniteScroll]',
})
export class InfiniteScrollDirective implements OnInit {
  readonly #elementRef = inject(ElementRef);
  readonly #destroyRef = inject(DestroyRef);
  readonly #document = inject(DOCUMENT);

  threshold = input<number>(50);
  hasMore = input<boolean>(true);
  isLoading = input<boolean>(false);
  useGlobalScroll = input(false, { transform: booleanAttribute });

  scrolled = output<void>();
  scrollInfo = output<ScrollInfo>();

  ngOnInit(): void {
    const target = this.useGlobalScroll()
      ? this.#document
      : this.#elementRef.nativeElement;

    fromEvent<Event>(target, 'scroll')
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        tap(({ target }) => {
          const { scrollingElement } = target as Document;
          this.#scroll((scrollingElement || target) as HTMLElement);
        }),
      )
      .subscribe();
  }

  #scroll(target: HTMLElement): void {
    const { scrollTop, scrollHeight, clientHeight } = target;
    const atBottom =
      scrollTop + clientHeight >= scrollHeight - this.threshold();

    this.scrollInfo.emit({ top: scrollTop, atBottom });

    if (this.isLoading() || !this.hasMore()) return;
    if (atBottom) this.scrolled.emit();
  }
}
